import { Task, Update, defModule, type TaskMessage, type UpdateResult } from '@st/redux'
import type { CreateFoldersResult, FolderImportPreview, SDKRequestFor } from '@st/sdk'
import { setToggle } from '@st/util/array-set'
import { minDuration } from '@st/util/effect'
import { Effect, pipe } from 'effect'
import { isMatching, match, P } from '@st/pattern'
import { SdkClient } from '~/effects'
import { observeRoute, runSdkRequest } from '~/features/module-runtime'
import {
  FOLDER_IMPORT_POLL_INTERVAL,
  isFolderImportReady,
  selDefaultJobIds
} from './folder-import-state'

type ImportSession = {
  importId: string
}

type ImportSelection = {
  selectedJobIds: string[]
  importState: FolderImportPreview
}

export type RunFolderImportState =
  | { step: 'idle' }
  | (ImportSession & { step: 'loading'; importState?: FolderImportPreview })
  | (ImportSession & { step: 'loadFailed' })
  | (ImportSession & ImportSelection & { step: 'selectFolders' })
  | (ImportSession & ImportSelection & { step: 'running' })
  | (ImportSession &
      ImportSelection & {
        step: 'finishing'
        nextImport: ImportSession | null
      })
  | (ImportSession & ImportSelection & { step: 'createFailed' })
  | (ImportSession &
      ImportSelection & {
        step: 'completed'
        createFoldersResult: CreateFoldersResult
      })

const observeImportRoute = observeRoute({ name: 'run_import' })

export type RunFolderImportMessage =
  // Browser location changed; this opens, replaces, or closes the import session.
  | TaskMessage<typeof observeImportRoute>
  // The state-owned import observation produced a new snapshot.
  | { type: 'importStateLoaded'; importState: FolderImportPreview }
  // The state-owned import observation failed and may be retried safely.
  | { type: 'importStateLoadFailed' }
  | { type: 'retryLoad' }
  | { type: 'toggleJobSelected'; jobId: string }
  | { type: 'toggleSelectAll' }
  // Accept the durable folder-creation Command.
  | { type: 'runImport' }
  | {
      type: 'foldersCreated'
      result: CreateFoldersResult
    }
  | { type: 'folderCreationFailed' }

type FolderCreationOutcome = Extract<
  RunFolderImportMessage,
  { type: 'foldersCreated' | 'folderCreationFailed' }
>

type ImportObservationMessage = Extract<
  RunFolderImportMessage,
  { type: 'importStateLoaded' | 'importStateLoadFailed' }
>

type SelectionMessage = Extract<
  RunFolderImportMessage,
  { type: 'toggleJobSelected' | 'toggleSelectAll' }
>

const observeImportState = Task.define((importId: string) =>
  Task.pipe(
    runSdkRequest(fetchImportState(importId)),
    Task.map((result) => {
      if (!result.ok) return { type: 'importStateLoadFailed' } as const
      return {
        type: 'importStateLoaded',
        importState: result.response
      } as const
    }),
    Task.periodic(FOLDER_IMPORT_POLL_INTERVAL)
  )
)

/**
 * Import flow topology:
 *
 * idle → loading → selectFolders → running → completed
 *          ↓                         ↓
 *      loadFailed              createFailed
 *          └── retry ──→ loading
 *
 * Route observation is always present. Polling exists only during `loading`,
 * so leaving that phase revokes both the current request and future polls.
 * Folder creation is a Command because accepting it starts durable writes.
 * Leaving its route hides the wizard but retains a `finishing` phase until the
 * write settles. The phase itself owns the outcome, so no request identity is
 * copied through the message protocol.
 */
export const runFolderImportModule = defModule<{
  state: RunFolderImportState
  message: RunFolderImportMessage
}>({
  init: () => ({ step: 'idle' }),
  update: pipe(
    updateRunFolderImport,
    Update.ignoreWhen<RunFolderImportState, RunFolderImportMessage>(
      isMatching({ state: { step: 'running' }, message: { type: 'runImport' } })
    )
  ),
  tasks: (state) => [
    observeImportRoute(),
    ...match(state)
      .with({ step: 'loading' }, ({ importId }) => [observeImportState(importId)])
      .with(
        {
          step: P.union(
            'idle',
            'loadFailed',
            'selectFolders',
            'running',
            'finishing',
            'createFailed',
            'completed'
          )
        },
        () => []
      )
      .exhaustive()
  ]
})

function updateRunFolderImport(
  state: RunFolderImportState,
  message: RunFolderImportMessage
): UpdateResult<RunFolderImportState, RunFolderImportMessage> {
  return match(message)
    .returnType<UpdateResult<RunFolderImportState, RunFolderImportMessage>>()
    .with({ type: 'routeChanged' }, ({ route }) => updateRoute(state, route))
    .with({ type: 'importStateLoaded' }, (message) => updateImportObservation(state, message))
    .with({ type: 'importStateLoadFailed' }, (message) => updateImportObservation(state, message))
    .with({ type: 'retryLoad' }, (message) =>
      match(state)
        .returnType<RunFolderImportState>()
        .with({ step: 'loadFailed' }, (state) => ({ ...state, step: 'loading' }) as const)
        .with(
          {
            step: P.union(
              'idle',
              'loading',
              'selectFolders',
              'running',
              'finishing',
              'createFailed',
              'completed'
            )
          },
          (state) => unexpectedMessage(state, message)
        )
        .exhaustive()
    )
    .with({ type: 'toggleJobSelected' }, (message) => updateSelection(state, message))
    .with({ type: 'toggleSelectAll' }, (message) => updateSelection(state, message))
    .with({ type: 'runImport' }, (message) => startImport(state, message))
    .with({ type: 'foldersCreated' }, (message) => updateFolderCreation(state, message))
    .with({ type: 'folderCreationFailed' }, (message) => updateFolderCreation(state, message))
    .exhaustive()
}

function updateRoute(
  state: RunFolderImportState,
  route: TaskMessage<typeof observeImportRoute>['route']
): RunFolderImportState {
  const target = match(route)
    .with(null, () => null)
    .otherwise((route) => ({ importId: route.importId }))

  return match(state)
    .returnType<RunFolderImportState>()
    .with({ step: 'running' }, (state) => {
      if (target?.importId == state.importId) return state
      return { ...state, step: 'finishing', nextImport: target }
    })
    .with({ step: 'finishing' }, (state) => {
      if (target?.importId == state.importId) {
        const { nextImport: _, ...running } = state
        return { ...running, step: 'running' }
      }
      return { ...state, nextImport: target }
    })
    .with({ step: 'idle' }, (state) => {
      if (!target) return state
      return {
        step: 'loading',
        ...target
      }
    })
    .with(
      {
        step: P.union('loading', 'loadFailed', 'selectFolders', 'createFailed', 'completed')
      },
      (state) => {
        if (!target) return { step: 'idle' }
        if (state.importId == target.importId) return state
        return {
          step: 'loading',
          ...target
        }
      }
    )
    .exhaustive()
}

function updateImportObservation(
  state: RunFolderImportState,
  message: ImportObservationMessage
): RunFolderImportState {
  return match({ state, message })
    .returnType<RunFolderImportState>()
    .with(
      { state: { step: 'loading' }, message: { type: 'importStateLoaded' } },
      ({ state, message }) => {
        if (!isFolderImportReady(message.importState))
          return { ...state, importState: message.importState }
        return {
          ...state,
          step: 'selectFolders',
          importState: message.importState,
          selectedJobIds: selDefaultJobIds(message.importState.folders)
        }
      }
    )
    .with(
      { state: { step: 'loading' }, message: { type: 'importStateLoadFailed' } },
      ({ state }) => ({ ...state, step: 'loadFailed' })
    )
    .with(
      {
        state: {
          step: P.union(
            'idle',
            'loadFailed',
            'selectFolders',
            'running',
            'finishing',
            'createFailed',
            'completed'
          )
        }
      },
      ({ state, message }) => unexpectedMessage(state, message)
    )
    .exhaustive()
}

function updateSelection(
  state: RunFolderImportState,
  message: SelectionMessage
): RunFolderImportState {
  return match({ state, message })
    .returnType<RunFolderImportState>()
    .with(
      { state: { step: 'selectFolders' }, message: { type: 'toggleJobSelected' } },
      ({ state, message }) => ({
        ...state,
        selectedJobIds: setToggle(state.selectedJobIds, message.jobId)
      })
    )
    .with(
      { state: { step: 'selectFolders' }, message: { type: 'toggleSelectAll' } },
      ({ state }) => ({
        ...state,
        selectedJobIds: selAllJobsSelected(state) ? [] : selDefaultJobIds(state.importState.folders)
      })
    )
    .with(
      {
        state: {
          step: P.union(
            'idle',
            'loading',
            'loadFailed',
            'running',
            'finishing',
            'createFailed',
            'completed'
          )
        }
      },
      ({ state, message }) => unexpectedMessage(state, message)
    )
    .exhaustive()
}

function startImport(
  state: RunFolderImportState,
  message: Extract<RunFolderImportMessage, { type: 'runImport' }>
): UpdateResult<RunFolderImportState, RunFolderImportMessage> {
  return match(state)
    .returnType<UpdateResult<RunFolderImportState, RunFolderImportMessage>>()
    .with({ step: 'selectFolders' }, (state) => [
      { ...state, step: 'running' },
      createFoldersFromImportEffect(state.importId, state.selectedJobIds)
    ])
    .with(
      {
        step: P.union('idle', 'loading', 'loadFailed', 'finishing', 'createFailed', 'completed')
      },
      (state) => unexpectedMessage(state, message)
    )
    .otherwise((state) => unexpectedMessage(state, message))
}

function updateFolderCreation(
  state: RunFolderImportState,
  message: FolderCreationOutcome
): RunFolderImportState {
  return match(state)
    .returnType<RunFolderImportState>()
    .with({ step: 'finishing' }, (state) => {
      if (state.nextImport) return { step: 'loading', ...state.nextImport }
      return { step: 'idle' }
    })
    .with({ step: 'running' }, (state) =>
      match(message)
        .returnType<RunFolderImportState>()
        .with({ type: 'foldersCreated' }, ({ result }) => ({
          ...state,
          step: 'completed',
          createFoldersResult: result
        }))
        .with({ type: 'folderCreationFailed' }, () => ({ ...state, step: 'createFailed' }))
        .exhaustive()
    )
    .otherwise((state) => unexpectedMessage(state, message))
}

function unexpectedMessage(state: RunFolderImportState, message: RunFolderImportMessage): never {
  throw new Error(`Unexpected folder import message ${message.type} while ${state.step}`)
}

export function selAllJobsSelected(
  state: Extract<RunFolderImportState, { importState: FolderImportPreview }>
) {
  const defaultJobIds = selDefaultJobIds(state.importState.folders)
  if (defaultJobIds.length == 0) return state.selectedJobIds.length == 0
  const selected = new Set(state.selectedJobIds)
  return defaultJobIds.every((id) => selected.has(id))
}

function fetchImportState(importId: string) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    return yield* sdk.send({
      type: 'folderGeneration/getFolderImportPreview',
      taxReturnImportId: importId
    } satisfies SDKRequestFor<'folderGeneration/getFolderImportPreview'>)
  })
}

function createFoldersFromImportEffect(importId: string, jobIds: string[]) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const created = yield* runSdkRequest(
      sdk.send({
        type: 'folderGeneration/createFolders',
        taxReturnImportId: importId,
        taxReturnIds: jobIds
      } satisfies SDKRequestFor<'folderGeneration/createFolders'>)
    ).pipe(minDuration('6 seconds'))
    if (!created.ok) return { type: 'folderCreationFailed' } as const

    return { type: 'foldersCreated', result: created.response } as const
  })
}
